import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from "../styles/users.module.css";
import ld from '../images/load.gif';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const navigate = useNavigate();
  const { users: currentUser } = useSelector((state) => state);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://chatting-club-backend.onrender.com/api/users');
      
      if (response.data.success) {
        const allUsers = response.data.users;
        const filteredUsersList = allUsers.filter(
          user => user._id !== currentUser?.userdata?._id
        );
        setUsers(filteredUsersList);
        setFilteredUsers(filteredUsersList);
      } else {
        toast.error('Failed to fetch users', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
    } finally {
      setLoading(false);
    }
  };

  const startChat = async (userId, userName) => {
    try {
      const senderId = currentUser?.userdata?._id;
      
      const response = await axios.post('https://chatting-club-backend.onrender.com/api/chats', {
        sender: senderId,
        receiver: userId
      });

      if (response.data.success) {
        const chatId = response.data.chat;
        navigate(`/chat/${chatId}`, { 
          state: { 
            userName,
            userId 
          } 
        });
      } else {
        toast.error(response.data.message || 'Failed to create chat', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (name) => {
    const colors = [
      '#9F7BFF', '#FF6B9D', '#4ADE80', '#60A5FA',
      '#FBBF24', '#F472B6', '#34D399', '#A78BFA',
      '#FB923C', '#22D3EE', '#F87171', '#818CF8'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div className={styles.usersContainer}>
      <div className={styles.usersWrapper}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.pageTitle}>💬 Users</h1>
            <p className={styles.pageSubtitle}>
              {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} available
            </p>
          </div>
          
          <div className={styles.searchWrapper}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search users by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className={styles.searchIcon}>🔍</span>
          </div>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <img src={ld} className={styles.loaderImage} alt="Loading users..." />
            <p className={styles.loadingText}>Loading users...</p>
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className={styles.usersGrid}>
            {filteredUsers.map((user) => {
              return (
                <div key={user._id} className={styles.userCard}>
                  <div className={styles.userCardContent}>
                    <div className={styles.avatarWrapper}>
                      <div 
                        className={styles.userAvatar} 
                        style={{ backgroundColor: getRandomColor(user.name) }}
                      >
                        {getInitials(user.name)}
                      </div>
                    </div>
                    
                    <div className={styles.userInfo}>
                      <h3 className={styles.userName}>{user.name}</h3>
                    </div>
                  </div>
                  
                  <button
                    className={styles.chatButton}
                    onClick={() => startChat(user._id, user.name)}
                  >
                    💬 Chat
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>👥</div>
            <h3 className={styles.emptyTitle}>No Users Found</h3>
            <p className={styles.emptyText}>
              {searchTerm.trim() !== '' 
                ? `No users found matching "${searchTerm}"`
                : 'There are no other users available to chat with right now.'}
            </p>
            {searchTerm.trim() !== '' && (
              <button 
                className={styles.clearSearchBtn}
                onClick={() => setSearchTerm('')}
              >
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;